import React from 'react'

// Standalone ROM band dial — the colored green/yellow/blue sectors, the
// red tick indicator, and the angle-with-degree text. Same coordinate
// system as KneeROMFigure (viewBox 0 0 230 240) so callers that overlay
// this on the PNG image stack get visually consistent bands.
//
// Props:
//   liveAngle    — clinical degrees
//   bandRanges   — { green:[a,b], yellow:[a,b], blue:[a,b] } | null
//   showTick     — render the red arrow at the outer band edge (default true)
//   showLabel    — render the angle text past the tick (default true)
//   noClamp      — disable the [-15, 175] safety clamp on the live angle
//   posture      — 'supine' flips the dial vertically so bands sweep
//                  UPWARD from the knee (matches a horizontal body PNG
//                  where the knee bends up — Knee to Chest, Heel Slide,
//                  etc.). Default opens downward for seated / standing.

// Defaults match the SittingLift PNG anchor. Real callers should pass
// per-kind anchors via the `kneeX` / `kneeY` / `bandR` props (see
// dialAnchorFor() in business/exercise/exerciseAvatar.js) — each PNG
// set puts the knee at a different fraction of the canvas, so a single
// shared anchor cannot align across exercises.
const DEFAULT_KNEE_X = 91
const DEFAULT_KNEE_Y = 134
const DEFAULT_BAND_R = 104

const toRad = (deg) => (deg * Math.PI) / 180
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))

export default function RomBandDial({
  liveAngle,
  bandRanges,
  showTick = true,
  showLabel = true,
  noClamp = true,
  posture = null,
  // Draw the dial mirror-imaged whatever the posture — the same geometry
  // 'supine' uses, so angles sweep counter-clockwise. For an upright figure
  // whose knee bends the other way round the pivot; pair it with angleOffset
  // to choose where 0° points. Set from a per-kind anchor (Mini Squat).
  mirrored = false,
  kneeX = DEFAULT_KNEE_X,
  kneeY = DEFAULT_KNEE_Y,
  bandR = DEFAULT_BAND_R,
  // Degrees added to the live angle + band edges before mapping to SVG
  // space — compensates when the PNG draws the leg at a non-zero angle for
  // liveAngle=0. Applies to BOTH the tick rotation AND the band sectors,
  // so the declared bandRanges stay semantic while their visual positions
  // rotate in lockstep with the arrow.
  angleOffset = 0,
  // Optional translation applied ONLY to the colored band <g> — lets a
  // caller nudge the band apex without moving the tick rotation center
  // (which must stay on the PNG knee for the live angle to read right).
  bandOffsetX = 0,
  bandOffsetY = 0,
  // Optional SVG viewBox override. Defaults render '0 0 230 240'.
  // Shifting the origin (viewBoxX/Y) translates all content; shrinking
  // the width/height (viewBoxW/H) zooms in on a sub-region of the dial.
  viewBoxX = 0,
  viewBoxY = 0,
  viewBoxW = 230,
  viewBoxH = 240,
  // Optional translation applied ONLY to the red tick arrow — lets a
  // caller shift the arrow's anchor independently of the bands.
  // tickRadiusOffset shifts the arrow polygon along its own rotated X
  // axis (closer to / further from the pivot) without changing bandR.
  tickOffsetX = 0,
  tickOffsetY = 0,
  tickRadiusOffset = 0,
  // Weekly-goal markers drawn ON TOP of the bands. Array of internal degrees
  // (extension + flexion targets); each renders as a THIN light-green sector
  // with a rotated "WEEKLY GOAL" label. Mirrors the patient Progress Test dial.
  goalAngles = null,
  // When true, the yellow ROM zone renders as a dark-blue DOTTED outer arc
  // (no radial sides / fill) instead of a filled sector. Green/blue unaffected.
  yellowOutline = false,
  // Optional radial DOTTED markers — array of internal degrees, or objects
  // { angle, value, caption }. Each is a dark-blue dotted line from the apex to
  // just past the band edge (e.g. the patient's last measured ext + flexion).
  radialAngles = null,
}) {
  const KNEE_X = kneeX
  const KNEE_Y = kneeY
  const BAND_R = bandR
  const hasLive = liveAngle !== null && liveAngle !== undefined
  const raw = hasLive ? liveAngle : 90
  const displayAngle = noClamp ? raw : clamp(raw, -15, 175)
  const curRad = toRad(displayAngle)

  // Supine posture: invert the Y direction so angles sweep UPWARD from
  // the knee anchor instead of downward. Patient lies horizontal, shin
  // starts pointing right (0°), rotates counterclockwise up toward the
  // chest as flexion increases.
  const isSupine = posture === 'supine' || mirrored === true
  const yDir = isSupine ? -1 : 1
  // Visual rotation of the tick (degrees, SVG convention where positive
  // = clockwise). In supine mode the tick rotates counterclockwise as
  // the angle increases.
  const tickRotateDeg = isSupine ? -(displayAngle + angleOffset) : (displayAngle + angleOffset)

  // Degree label is pinned to a single spot at the center-bottom
  // (seated) or center-top (supine) of the dial — does NOT rotate with
  // the live-angle tick. Patient always reads the number from the
  // same place, mirroring the reference mockup.
  const FIXED_LABEL_OFFSET = BAND_R + 12
  const labelX = KNEE_X.toFixed(1)
  const labelY = (KNEE_Y + yDir * FIXED_LABEL_OFFSET).toFixed(1)

  const sector = (a1, a2) => {
    const r1 = toRad(a1 + angleOffset), r2 = toRad(a2 + angleOffset)
    const sx = (KNEE_X + BAND_R * Math.cos(r1)).toFixed(1)
    const sy = (KNEE_Y + yDir * BAND_R * Math.sin(r1)).toFixed(1)
    const ex = (KNEE_X + BAND_R * Math.cos(r2)).toFixed(1)
    const ey = (KNEE_Y + yDir * BAND_R * Math.sin(r2)).toFixed(1)
    const large = (a2 - a1) > 180 ? 1 : 0
    // Arc sweep flag also flips when Y is inverted, otherwise the
    // sector renders inside-out (the wrong half of the circle).
    const sweep = isSupine ? 0 : 1
    return `M ${KNEE_X} ${KNEE_Y} L ${sx} ${sy} A ${BAND_R} ${BAND_R} 0 ${large} ${sweep} ${ex} ${ey} Z`
  }

  // Just the outer arc of a sector (no radial edges, not closed) — used for
  // the Progress Test's dotted yellow outline so only the perimeter is drawn.
  const arc = (a1, a2) => {
    const r1 = toRad(a1 + angleOffset), r2 = toRad(a2 + angleOffset)
    const sx = (KNEE_X + BAND_R * Math.cos(r1)).toFixed(1)
    const sy = (KNEE_Y + yDir * BAND_R * Math.sin(r1)).toFixed(1)
    const ex = (KNEE_X + BAND_R * Math.cos(r2)).toFixed(1)
    const ey = (KNEE_Y + yDir * BAND_R * Math.sin(r2)).toFixed(1)
    const large = (a2 - a1) > 180 ? 1 : 0
    const sweep = isSupine ? 0 : 1
    return `M ${sx} ${sy} A ${BAND_R} ${BAND_R} 0 ${large} ${sweep} ${ex} ${ey}`
  }

  // A single radial line from the dial apex out to just past the band edge at
  // internal angle `a` (1.10·R extends it ~10% past the arc so it crosses it).
  const radial = (a) => {
    const r = toRad(a + angleOffset)
    const R = BAND_R * 1.10
    const ex = (KNEE_X + R * Math.cos(r)).toFixed(1)
    const ey = (KNEE_Y + yDir * R * Math.sin(r)).toFixed(1)
    return `M ${KNEE_X} ${KNEE_Y} L ${ex} ${ey}`
  }

  return (
    <svg
      viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxW} ${viewBoxH}`}
      width='100%'
      height='100%'
      style={{ display: 'block', overflow: 'visible' }}
    >
      {bandRanges && (
        <g transform={`translate(${bandOffsetX} ${bandOffsetY})`}>
          {bandRanges.green  && <path d={sector(bandRanges.green[0],  bandRanges.green[1])}  fill='#669d34' opacity='0.85' />}
          {bandRanges.yellow && (yellowOutline
            ? <path d={arc(bandRanges.yellow[0], bandRanges.yellow[1])} fill='none' stroke='#012448' strokeWidth='1.25' strokeDasharray='1 4' strokeLinecap='round' strokeLinejoin='round' />
            : <path d={sector(bandRanges.yellow[0], bandRanges.yellow[1])} fill='#fefb41' opacity='0.9' />)}
          {bandRanges.blue   && <path d={sector(bandRanges.blue[0],   bandRanges.blue[1])}   fill='#bdb6b6' opacity='0.95' />}
        </g>
      )}

      {/* Last-measurement radial markers — dotted dark-blue lines from the apex
          to just past the band edge, optionally with a value + caption badge. */}
      {radialAngles && radialAngles.length > 0 && (
        <g transform={`translate(${bandOffsetX} ${bandOffsetY})`}>
          {radialAngles.map(function (m, i) {
            var a = (typeof m === 'number') ? m : m.angle
            var hasLabel = (typeof m === 'object') && m !== null && m.value != null
            var r = toRad(a + angleOffset)
            var LR = BAND_R * 1.15
            var lx = (KNEE_X + LR * Math.cos(r))
            var ly = (KNEE_Y + yDir * LR * Math.sin(r)) + BAND_R * 0.10
            var words = hasLabel && m.caption ? String(m.caption).split(' ') : []
            return (
              <g key={'radial-' + i}>
                <path
                  d={radial(a)}
                  fill='none'
                  stroke='#012448'
                  strokeWidth='1.25'
                  strokeDasharray='1 4'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                {hasLabel && (
                  <g fill='#012448' textAnchor='middle'>
                    <text x={lx.toFixed(1)} y={(ly - 4).toFixed(1)} fontSize='11' fontWeight='bold'>
                      {m.value}°
                    </text>
                    {words.map(function (w, j) {
                      return (
                        <text key={'w-' + j} x={lx.toFixed(1)} y={(ly + 4 + j * 6).toFixed(1)} fontSize='5' fontWeight='bold' letterSpacing='0.3'>
                          {w}
                        </text>
                      )
                    })}
                  </g>
                )}
              </g>
            )
          })}
        </g>
      )}

      {/* Weekly-goal markers — a THIN light-green sector at EACH goal angle with
          a rotated "WEEKLY GOAL" label running parallel to the band. */}
      {goalAngles && goalAngles.length > 0 && (
        <g>
          {goalAngles.map(function (a, i) {
            var gr = toRad(a + angleOffset)
            var GR = BAND_R * 0.80
            var gx = (KNEE_X + GR * Math.cos(gr))
            var gy = (KNEE_Y + yDir * GR * Math.sin(gr))
            var rot = isSupine ? -(a + angleOffset) : (a + angleOffset)
            var norm = ((rot % 360) + 360) % 360
            var flip = (norm > 90 && norm < 270) ? 180 : 0
            return (
              <g key={'goal-' + i}>
                <path d={sector(a - 2.5, a + 2.5)} fill='#669d34' opacity='0.9' />
                <text
                  x={gx.toFixed(1)} y={gy.toFixed(1)}
                  transform={`rotate(${(rot + flip).toFixed(1)} ${gx.toFixed(1)} ${gy.toFixed(1)})`}
                  fontSize='6' fontWeight='bold' fill='#33691e'
                  textAnchor='middle' dominantBaseline='middle' letterSpacing='0.3'
                >
                  WEEKLY GOAL
                </text>
              </g>
            )
          })}
        </g>
      )}

      {showTick && hasLive && (
        <g transform={`translate(${KNEE_X + tickOffsetX} ${KNEE_Y + tickOffsetY}) rotate(${tickRotateDeg.toFixed(2)})`}>
          <polygon
            points={`${BAND_R + tickRadiusOffset + 6},0 ${BAND_R + tickRadiusOffset + 18},-5 ${BAND_R + tickRadiusOffset + 18},5`}
            fill='#e53935'
            stroke='#b71c1c'
            strokeWidth='0.75'
            strokeLinejoin='round'
          />
        </g>
      )}

      {showLabel && hasLive && (
        <text
          x={labelX}
          y={labelY}
          fontSize='15'
          fill='#333'
          fontWeight='bold'
          textAnchor='middle'
          dominantBaseline='middle'
        >
          {displayAngle.toFixed(0)}°
        </text>
      )}
    </svg>
  )
}
